import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { PanelId, useAppDrawerStore } from './useAppDrawerStore'
import { theme } from '@/Atomic/assets/theme'

const PANEL_WIDTH = '420px'

const PANEL_ORDER: PanelId[] = ['logTime', 'chatbot']

const expandWidth = keyframes`
  from { width: 0; }
  to   { width: ${PANEL_WIDTH}; }
`

const collapseWidth = keyframes`
  from { width: ${PANEL_WIDTH}; }
  to   { width: 0; }
`

const Panel = styled.aside<{ isClosing: boolean }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  width: ${PANEL_WIDTH};
  overflow: hidden;
  background: ${theme.colors.background.default};
  border-left: 1px solid ${theme.colors.border.line};
  animation: ${({ isClosing }) =>
    isClosing
      ? css`
          ${collapseWidth} 0.35s cubic-bezier(0.4, 0, 0.8, 0.6) forwards
        `
      : css`
          ${expandWidth} 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards
        `};
`

// Fixed width keeps content stable while outer animates to 0 — prevents reflow during slide
const StableContentBox = styled.div`
  display: flex;
  flex-direction: column;
  width: ${PANEL_WIDTH};
  height: 100%;
`

export function PushDrawer() {
  const { panels, closing } = useAppDrawerStore(({ panels, closing }) => ({
    panels,
    closing,
  }))
  const { close, animationDidEnd } = useAppDrawerStore.getState()

  const visible = PANEL_ORDER.flatMap((id) => {
    const open = panels.find((p) => p.id === id)
    if (open) return [{ ...open, isClosing: false }]
    const leaving = closing.find((p) => p.id === id)
    return leaving ? [{ ...leaving, isClosing: true }] : []
  })

  if (visible.length === 0) return null

  return (
    <>
      {visible.map(({ id, render, isClosing }) => (
        <Panel
          key={id}
          isClosing={isClosing}
          onAnimationEnd={() => isClosing && animationDidEnd(id)}
        >
          <StableContentBox>{render(() => close(id))}</StableContentBox>
        </Panel>
      ))}
    </>
  )
}
